/**
 * Formatiert die Durchführungszeit einer Maßnahme als kompakten Text für
 * die Tabellenanzeige. Gruppiert aufeinanderfolgende Wochentage mit
 * gleichen Zeiten (z. B. "Mo–Do 7:15-16 / Fr 7:15-15").
 *
 * Rückgabe-Objekt:
 *   { primary: string, badges: Array<{ label, time }>, isEmpty: boolean }
 */
const WEEKDAY_LABELS = {
  mon: 'Mo', tue: 'Di', wed: 'Mi', thu: 'Do', fri: 'Fr', sat: 'Sa', sun: 'So',
};
const WEEKDAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const compact = (t) => (t || '').replace(/^0/, '').replace(/:00$/, ''); // "07:15" → "7:15", "16:00" → "16"

export const formatMassnahmeZeit = (massnahme) => {
  const wt = massnahme?.weekday_times;
  const hasWeekdays = wt && typeof wt === 'object' && Object.keys(wt).some(
    (k) => wt[k] && (wt[k].start || wt[k].end)
  );

  if (hasWeekdays) {
    // Gruppiere: aufeinanderfolgende Tage mit gleicher Zeit zusammenfassen
    const days = WEEKDAY_ORDER
      .filter((k) => wt[k] && wt[k].start && wt[k].end)
      .map((k) => ({ key: k, start: wt[k].start, end: wt[k].end }));
    if (days.length === 0) {
      return { primary: '—', badges: [], isEmpty: true };
    }
    const groups = [];
    days.forEach((d) => {
      const last = groups[groups.length - 1];
      const sameTime = last && last.start === d.start && last.end === d.end;
      const idxLast = last && WEEKDAY_ORDER.indexOf(last.keys[last.keys.length - 1]);
      const idxCur = WEEKDAY_ORDER.indexOf(d.key);
      const consecutive = last && idxCur === idxLast + 1;
      if (sameTime && consecutive) {
        last.keys.push(d.key);
      } else {
        groups.push({ keys: [d.key], start: d.start, end: d.end });
      }
    });
    const badges = groups.map((g) => {
      const label = g.keys.length === 1
        ? WEEKDAY_LABELS[g.keys[0]]
        : `${WEEKDAY_LABELS[g.keys[0]]}–${WEEKDAY_LABELS[g.keys[g.keys.length - 1]]}`;
      return { label, time: `${compact(g.start)}–${compact(g.end)}` };
    });
    return {
      primary: badges.map((b) => `${b.label} ${b.time}`).join(' / '),
      badges,
      isEmpty: false,
    };
  }

  if (massnahme?.start_time && massnahme?.end_time) {
    return {
      primary: `${massnahme.start_time}–${massnahme.end_time}`,
      badges: [{ label: 'Mo–Fr', time: `${compact(massnahme.start_time)}–${compact(massnahme.end_time)}` }],
      isEmpty: false,
    };
  }

  return { primary: '—', badges: [], isEmpty: true };
};

// getUTCDay(): 0=So .. 6=Sa  →  Wochentag-Schlüssel der weekday_times
const WD_KEYS_BY_UTCDAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Löst die gültige Maßnahmen-Zeit für ein konkretes Datum auf (Frontend-Pendant
 * zu backend/utils/massnahme_times.resolve_massnahme_times):
 *   1) Wochentag-spezifische Zeit (weekday_times[wd])
 *   2) Einheitliche Maßnahmen-Zeit (start_time/end_time)
 *   3) leer
 * Damit übernimmt „Neuer Einsatz" z. B. freitags 15:00 statt der Mo–Do-Zeit.
 */
export const resolveMassnahmeTimesForDate = (massnahme, isoDate) => {
  if (!massnahme) return { start: '', end: '' };
  const wt = massnahme.weekday_times;
  if (isoDate && wt && typeof wt === 'object') {
    const [y, m, d] = String(isoDate).split('-').map(Number);
    if (y && m && d) {
      const idx = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
      const day = wt[WD_KEYS_BY_UTCDAY[idx]];
      if (day && day.start && day.end) {
        return { start: day.start, end: day.end };
      }
    }
  }
  if (massnahme.start_time && massnahme.end_time) {
    return { start: massnahme.start_time, end: massnahme.end_time };
  }
  return { start: '', end: '' };
};
